/**
 * AdminUsersView - the Team & Access page.
 *
 * Lists everyone in the users registry (auto-populated as people sign in via
 * SSO). A role is a starting template; each person can have per-area
 * overrides on top (the "Access" panel, components/team/AccessPanel).
 *
 * Who may change anything (Sharif, 10/5/26): only people who MANAGE ACCESS -
 * the bootstrap admin, and whoever has the "Can manage access" switch on.
 * Everyone else who can open this page sees it read-only: no Access button,
 * the role shown but not changeable. The server enforces the same
 * (server/routes/users.js); this only avoids offering what it would refuse.
 */
import { useState } from 'react';
import { useApp } from '../context/AppContext';
import { useAuth } from '../context/AuthContext';
import { isAdmin as isBootstrapAdmin } from '../data/admins';
import { ROLE_OPTIONS, ROLE_LABELS, roleLabel, canSeeTab, canEdit, effectiveAccess } from '../data/roles';
import { useMyAccess } from '../hooks/useMyAccess';
import AccessPanel from '../components/team/AccessPanel';

const fmtDateTime = (iso) => {
  if (!iso) return 'Never';
  const [y, m, d] = String(iso).slice(0, 10).split('-');
  if (!y) return '-';
  const t = String(iso).slice(11, 16);
  return `${parseInt(m, 10)}/${parseInt(d, 10)}/${y.slice(2)}${t ? ` · ${t}` : ''}`;
};

/** Does this roster row's effective access differ from what its role alone gives? */
const hasOverrides = (u) => {
  if (!u.areas || isBootstrapAdmin(u.email)) return false;
  const plain = effectiveAccess(u.role, {}, { canManageAccess: !!u.canManageAccess }).areas;
  return Object.keys(plain).some((k) => u.areas[k] && u.areas[k] !== plain[k]);
};

const SELECT = {
  padding: '6px 10px',
  borderRadius: 6,
  fontSize: 12,
  fontFamily: 'inherit',
  fontWeight: 600,
  color: '#0D0D0D',
};
const BADGE = { marginLeft: 8, fontSize: 9.5, fontWeight: 800, borderRadius: 10, padding: '1px 7px', letterSpacing: 0.3 };

export default function AdminUsersView() {
  const { users, updateUserRole, allEngineers, refresh } = useApp();
  const { user } = useAuth();
  const access = useMyAccess();
  const canManage = !!access.canManageAccess;
  const canLink = canEdit(access, 'team');
  const [openFor, setOpenFor] = useState(null); // email whose Access panel is open
  const [notice, setNotice] = useState('');

  const changeRole = async (email, role) => {
    setNotice('');
    const ok = await updateUserRole(email, role);
    if (ok === false) setNotice(`Could not change the role of ${email}.`);
  };

  // Take someone out of GlobalOps (managers of access). Signing in again
  // brings them back as Pending, with no access.
  const removeUser = async (u) => {
    if (
      !window.confirm(`Remove ${u.name || u.email} from GlobalOps?

Their access ends now. Records they own keep their name. If they sign in again they come back as Pending, with no access.`)
    )
      return;
    setNotice('');
    try {
      const r = await fetch(`/api/users/${encodeURIComponent(u.email)}`, { method: 'DELETE' });
      if (!r.ok) {
        const d = await r.json().catch(() => ({}));
        setNotice(d.error || `Could not remove ${u.email}.`);
        return;
      }
      await refresh?.();
    } catch {
      setNotice(`Could not remove ${u.email}.`);
    }
  };

  // Link an engineer-role user to an engineers record: PUTs engineers.email so
  // the API (requireEngineer) can resolve their record. Refresh afterward so the
  // "linked engineer" badge updates.
  const linkEngineer = async (email, engineerName) => {
    try {
      await fetch('/api/engineer/link', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ engineerName, email }),
      });
      await refresh?.();
    } catch (err) {
      console.error('linkEngineer error:', err);
    }
  };

  // email (lowercase) -> linked engineer name, from the engineers roster.
  const linkedByEmail = {};
  for (const e of allEngineers || []) {
    const em = (e.email || '').toLowerCase();
    if (em) linkedByEmail[em] = e.name;
  }
  const engineerNames = [...new Set((allEngineers || []).map((e) => e.name).filter(Boolean))].sort((a, b) => a.localeCompare(b));

  if (!canSeeTab(access, 'team')) {
    return (
      <div style={{ padding: 40, textAlign: 'center', fontFamily: "'Montserrat', sans-serif" }}>
        <div style={{ fontSize: 16, fontWeight: 800, color: '#B71C1C', marginBottom: 6 }}>No access</div>
        <div style={{ fontSize: 12, color: '#666' }}>You don't have access to Team &amp; Access.</div>
      </div>
    );
  }

  const sorted = [...(users || [])].sort((a, b) => (a.name || a.email || '').localeCompare(b.name || b.email || ''));
  // Split out users awaiting a role assignment - surfaced in a dedicated section
  // at the top so a manager of access sees new sign-ins that still need attention.
  const pending = sorted.filter((u) => u.role === 'pending');
  const myEmail = (user?.email || '').toLowerCase();

  const TH = {
    textAlign: 'left',
    padding: '10px 14px',
    fontSize: 10,
    fontWeight: 800,
    color: '#888',
    letterSpacing: 1,
    textTransform: 'uppercase',
    borderBottom: '2px solid #1B5E20',
  };
  const TD = { padding: '12px 14px', fontSize: 12.5, color: '#0D0D0D', borderBottom: '1px solid #EEE' };

  return (
    <div style={{ fontFamily: "'Montserrat', sans-serif" }}>
      <div style={{ marginBottom: 16 }}>
        <div style={{ fontSize: 10, fontWeight: 700, color: '#1B5E20', letterSpacing: 2, textTransform: 'uppercase' }}>
          Admin · Team &amp; Access
        </div>
        <p style={{ fontSize: 12.5, color: '#555', margin: '4px 0 0' }}>
          <b className="tabular-nums">{sorted.length}</b> {sorted.length === 1 ? 'person has' : 'people have'} signed in.
          {canManage
            ? ' Roles and access update immediately; new staff appear here the first time they sign in with Microsoft.'
            : ' Only people who manage access can change roles or access - this page is read-only for you.'}
        </p>
      </div>

      {notice && (
        <div
          role="alert"
          style={{
            marginBottom: 12,
            background: '#FFEBEE',
            border: '1px solid #FFCDD2',
            color: '#B71C1C',
            borderRadius: 8,
            padding: '8px 12px',
            fontSize: 12,
            fontWeight: 600,
          }}
        >
          {notice}
        </div>
      )}

      {pending.length > 0 && (
        <div
          style={{
            marginBottom: 18,
            background: '#FFF8E1',
            border: '1px solid #FFE082',
            borderRadius: 12,
            overflow: 'hidden',
            boxShadow: '0 1px 4px rgba(0,0,0,0.04)',
          }}
        >
          <div style={{ padding: '12px 16px', borderBottom: '1px solid #FFE082', display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontSize: 13, fontWeight: 800, color: '#8D6E00' }}>Pending approval</span>
            <span
              style={{ fontSize: 11, fontWeight: 800, color: '#fff', background: '#F9A825', borderRadius: 10, padding: '1px 8px' }}
              className="tabular-nums"
            >
              {pending.length}
            </span>
          </div>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th style={{ ...TH, borderBottom: '2px solid #F9A825' }}>Name</th>
                <th style={{ ...TH, borderBottom: '2px solid #F9A825' }}>Email</th>
                <th style={{ ...TH, borderBottom: '2px solid #F9A825' }}>Signed in</th>
                <th style={{ ...TH, borderBottom: '2px solid #F9A825' }}>Assign role</th>
              </tr>
            </thead>
            <tbody>
              {pending.map((u) => (
                <tr key={u.email}>
                  <td style={{ ...TD, borderBottom: '1px solid #FFECB3' }}>
                    <span style={{ fontWeight: 700 }}>{u.name || '-'}</span>
                  </td>
                  <td style={{ ...TD, color: '#555', borderBottom: '1px solid #FFECB3' }}>{u.email}</td>
                  <td style={{ ...TD, color: '#666', borderBottom: '1px solid #FFECB3', fontVariantNumeric: 'tabular-nums' }}>
                    {fmtDateTime(u.lastLogin || u.last_login)}
                  </td>
                  <td style={{ ...TD, borderBottom: '1px solid #FFECB3' }}>
                    {canManage ? (
                      <select
                        aria-label={`Assign a role to ${u.email}`}
                        value=""
                        onChange={(e) => {
                          if (e.target.value) changeRole(u.email, e.target.value);
                        }}
                        style={{ ...SELECT, border: '1px solid #F9A825', background: '#fff', cursor: 'pointer' }}
                        title="Assign a role to grant access"
                      >
                        <option value="" disabled>
                          Assign role…
                        </option>
                        {ROLE_OPTIONS.map((v) => (
                          <option key={v} value={v}>
                            {ROLE_LABELS[v]}
                          </option>
                        ))}
                      </select>
                    ) : (
                      <span style={{ fontSize: 11.5, color: '#8D6E00' }}>Waiting for someone who manages access</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div
        style={{
          background: '#fff',
          border: '1px solid #ECECEC',
          borderRadius: 12,
          overflow: 'hidden',
          boxShadow: '0 1px 4px rgba(0,0,0,0.04)',
        }}
      >
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              <th style={TH}>Name</th>
              <th style={TH}>Email</th>
              <th style={TH}>Role</th>
              {canManage && <th style={TH}>Access</th>}
              <th style={TH}>Linked engineer</th>
              <th style={TH}>Last sign-in</th>
            </tr>
          </thead>
          <tbody>
            {sorted.length === 0 && (
              <tr>
                <td colSpan={canManage ? 6 : 5} style={{ ...TD, textAlign: 'center', color: '#aaa', padding: 32 }}>
                  No users yet - they'll appear here as they sign in.
                </td>
              </tr>
            )}
            {sorted.map((u) => {
              const email = (u.email || '').toLowerCase();
              const isSelf = email === myEmail;
              const locked = isBootstrapAdmin(u.email); // the bootstrap admin's role is fixed
              const custom = hasOverrides(u);
              const managesAccess = locked || !!u.canManageAccess;
              return (
                <tr key={u.email} data-testid={`user-${email}`}>
                  <td style={TD}>
                    <span style={{ fontWeight: 700 }}>{u.name || '-'}</span>
                    {isSelf && <span style={{ marginLeft: 8, fontSize: 9.5, color: '#1565C0', fontWeight: 700 }}>(you)</span>}
                    {managesAccess && (
                      <span style={{ ...BADGE, background: '#E8F5E9', color: '#1B5E20' }} title="May change roles and access">
                        Manages access
                      </span>
                    )}
                  </td>
                  <td style={{ ...TD, color: '#555' }}>{u.email}</td>
                  <td style={TD}>
                    {canManage && !locked ? (
                      <select
                        aria-label={`Role of ${u.email}`}
                        value={u.role === 'pending' ? '' : ROLE_LABELS[u.role] ? u.role : ''}
                        onChange={(e) => {
                          if (e.target.value) changeRole(u.email, e.target.value);
                        }}
                        style={{
                          ...SELECT,
                          border: `1px solid ${u.role === 'pending' ? '#F9A825' : '#D8DED4'}`,
                          background: u.role === 'pending' ? '#FFF8E1' : '#fff',
                          cursor: 'pointer',
                        }}
                        title={u.role === 'pending' ? 'Awaiting assignment - pick a role' : 'Change role (their access overrides are kept)'}
                      >
                        {(u.role === 'pending' || !ROLE_LABELS[u.role]) && (
                          <option value="" disabled>
                            {roleLabel(u.role)}
                          </option>
                        )}
                        {ROLE_OPTIONS.map((v) => (
                          <option key={v} value={v}>
                            {ROLE_LABELS[v]}
                          </option>
                        ))}
                      </select>
                    ) : (
                      <span style={{ fontWeight: 600 }} title={locked ? 'Bootstrap admin - fixed' : undefined}>
                        {locked ? ROLE_LABELS.admin : roleLabel(u.role)}
                      </span>
                    )}
                    {custom && (
                      <span
                        style={{ ...BADGE, background: '#FFF8E1', color: '#8D6E00', border: '1px solid #FFE082' }}
                        title="Has per-area overrides on top of the role"
                      >
                        Custom access
                      </span>
                    )}
                  </td>
                  {canManage && (
                    <td style={TD}>
                      <button
                        type="button"
                        onClick={() => setOpenFor(u.email)}
                        aria-label={`Access for ${u.email}`}
                        style={{
                          padding: '5px 12px',
                          borderRadius: 6,
                          border: '1px solid #1B5E20',
                          background: '#fff',
                          color: '#1B5E20',
                          fontFamily: 'inherit',
                          fontSize: 11.5,
                          fontWeight: 700,
                          cursor: 'pointer',
                        }}
                      >
                        Access
                      </button>
                      {!locked && !isSelf && (
                        <button
                          type="button"
                          onClick={() => removeUser(u)}
                          aria-label={`Remove ${u.email}`}
                          title="Take them out of GlobalOps"
                          style={{
                            marginLeft: 6,
                            padding: '5px 10px',
                            borderRadius: 6,
                            border: '1px solid #C62828',
                            background: '#fff',
                            color: '#C62828',
                            fontFamily: 'inherit',
                            fontSize: 11.5,
                            fontWeight: 700,
                            cursor: 'pointer',
                          }}
                        >
                          Remove
                        </button>
                      )}
                    </td>
                  )}
                  <td style={TD}>
                    {u.role === 'engineer' && canLink ? (
                      <select
                        value={linkedByEmail[email] || ''}
                        onChange={(e) => linkEngineer(email, e.target.value)}
                        style={{
                          ...SELECT,
                          border: `1px solid ${linkedByEmail[email] ? '#D8DED4' : '#F9A825'}`,
                          background: linkedByEmail[email] ? '#fff' : '#FFF8E1',
                          cursor: 'pointer',
                        }}
                        title="Link this engineer to their staff record so My Schedule / My Project resolve"
                      >
                        <option value="">Not linked…</option>
                        {engineerNames.map((n) => (
                          <option key={n} value={n}>
                            {n}
                          </option>
                        ))}
                      </select>
                    ) : u.role === 'engineer' ? (
                      <span style={{ fontSize: 11.5 }}>{linkedByEmail[email] || 'Not linked'}</span>
                    ) : (
                      <span style={{ color: '#bbb', fontSize: 11.5 }}>-</span>
                    )}
                  </td>
                  <td style={{ ...TD, color: '#666', fontVariantNumeric: 'tabular-nums' }}>{fmtDateTime(u.lastLogin || u.last_login)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div
        style={{
          marginTop: 14,
          padding: '10px 14px',
          background: '#F1F8E9',
          border: '1px solid #C8E6C9',
          borderRadius: 8,
          fontSize: 11,
          color: '#33691E',
          lineHeight: 1.5,
        }}
      >
        New sign-ins start as <b>No access yet</b> and can't see the app until someone who manages access assigns a role. A role is a
        starting point: <b>Access</b> sets any area to Hidden, View or Edit for one person (Edit includes delete), and those overrides stay
        when their role changes. Only people who <b>manage access</b> can change roles, access or that switch - nobody can switch it on for
        themselves.
      </div>

      {openFor && (
        <AccessPanel
          email={openFor}
          isSelf={openFor.toLowerCase() === myEmail}
          onClose={() => setOpenFor(null)}
          onChanged={() => refresh?.()}
        />
      )}
    </div>
  );
}
